import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ViewportList } from 'react-viewport-list';

import { filtersSelector } from 'store/modules/competition-nav/selectors/filters-selector';
import { useGetMatchesQuery } from 'store/modules/api/afl-api';
import { filterByCity } from '../../utils/filter-by-city';

import { EmptyState } from 'common/react/components/EmptyState';

import { FixturesItem } from './FixturesItem';
import { ByesGroup } from './ByesGroup';
import { FixturesSkeleton } from './FixturesSkeleton';
import { FixturesLegend } from './FixturesLegend';

import { getTranslation } from 'common/react/utils/translations';
import { useGroupMatches } from 'common/react/hooks/use-group-matches';

const LAST_BYE_ROUND = 23;
const MARGIN_BOTTOM_DESKTOP = 24;
const MARGIN_BOTTOM_MOBILE = 16;

const checkIfBroadcasterGuide = (providerId) => {
    const translationString = getTranslation(
        `label.broadcastGuideURL.${providerId}`
    );

    if (translationString !== `label.broadcastGuideURL.${providerId}`) {
        return translationString;
    }

    return false;
};

export const FixturesList = ({
    competitionId,
    compSeason,
    round,
    broadcasterPromo,
    scrollToRound,
    setScrollToRound
}) => {
    const filters = useSelector(filtersSelector);
    const selectedTimeZone = useSelector((state) => state.selectedRegion);
    const listRef = useRef(null);

    const disableRounds =
        filters?.cities.length > 0 ||
        filters?.teams.length > 0 ||
        filters?.venues.length > 0;

    const broadcastGuideUrl =
        checkIfBroadcasterGuide(compSeason.providerId) ?? '';

    const { matches = [], isMatchesFetching } = useGetMatchesQuery(
        {
            pageSize: 300,
            competitionId: competitionId,
            compSeasonId: compSeason.id,
            ...(!disableRounds && { roundNumber: round.roundNumber }),
            ...(filters.teams.length && { teamId: filters.teams }),
            ...(filters.venues.length && { venueId: filters.venues })
        },
        {
            selectFromResult: ({ data, isFetching }) => ({
                matches:
                    data && filters.cities.length
                        ? filterByCity(data, filters.cities)
                        : data,
                isMatchesFetching: isFetching
            })
        }
    );

    const matchItems = useGroupMatches(matches, selectedTimeZone.timezone);

    const roundNumber = round.roundNumber;

    const isConferenceSeason = matches.some(
        (match) => match?.home?.conference || match?.away?.conference
    );

    /**
     * If rounds are disabled (so we're showing multiple rounds) we should
     * listen for when the round changes, i.e. a round button is clicked, and
     * scroll the viewport list to that clicked round.
     */
    useEffect(() => {
        if (scrollToRound && disableRounds) {
            const targetIndex = matchItems.findIndex(
                (item) => item?.round?.roundNumber === scrollToRound
            );
            if (targetIndex && listRef.current) {
                listRef.current.scrollToIndex({
                    index: targetIndex,
                    offset: -160
                });
            }
            setScrollToRound(null);
        }
    }, [disableRounds, matchItems, scrollToRound, setScrollToRound]);

    if (isMatchesFetching) {
        return <FixturesSkeleton />;
    }

    if (!matches || !matches.length) {
        return <EmptyState cssClass="competition-nav__empty-state" />;
    }

    // find first live or pre-match fixture
    let firstLiveOrUpcomingFixture = {};
    firstLiveOrUpcomingFixture = [...matches]
        .reverse()
        .find(
            (match) =>
                PULSE.app.common.match.getMatchStatus(match.status) ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        );
    if (!firstLiveOrUpcomingFixture) {
        firstLiveOrUpcomingFixture = matches.find(
            (match) =>
                PULSE.app.common.match.getMatchStatus(match.status) ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING
        );
    }

    return (
        <>
            {matchItems?.length && disableRounds ? (
                <ViewportList
                    ref={listRef}
                    items={matchItems}
                    itemMargin={
                        window.matchMedia('(max-width: 640px)').matches
                            ? MARGIN_BOTTOM_MOBILE
                            : MARGIN_BOTTOM_DESKTOP
                    }
                    overscan={10}
                    renderSpacer={({ ref, style }) => (
                        <div ref={ref} style={style}>
                            <FixturesSkeleton></FixturesSkeleton>
                        </div>
                    )}
                >
                    {(item, index) => (
                        <FixturesItem
                            key={
                                typeof item === 'string'
                                    ? `${item}-${index}`
                                    : item.id
                            }
                            item={item}
                            broadcastGuideUrl={broadcastGuideUrl}
                            disableRounds={disableRounds}
                            broadcasterPromo={broadcasterPromo}
                            firstLiveOrUpcomingFixture={
                                firstLiveOrUpcomingFixture?.id
                            }
                        />
                    )}
                </ViewportList>
            ) : matchItems?.length ? (
                matchItems.map((item, index) => (
                    <FixturesItem
                        key={
                            typeof item === 'string'
                                ? `${item}-${index}`
                                : item.id
                        }
                        item={item}
                        broadcastGuideUrl={broadcastGuideUrl}
                        disableRounds={disableRounds}
                        broadcasterPromo={broadcasterPromo}
                        firstLiveOrUpcomingFixture={
                            firstLiveOrUpcomingFixture?.id
                        }
                    />
                ))
            ) : null}

            {!isNaN(roundNumber) &&
            roundNumber < LAST_BYE_ROUND &&
            !disableRounds &&
            compSeason.id ? (
                <ByesGroup
                    compseasonId={compSeason.id}
                    roundNumber={roundNumber}
                />
            ) : null}

            {isConferenceSeason && <FixturesLegend />}
        </>
    );
};

FixturesList.propTypes = {
    competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    compSeason: PropTypes.object,
    round: PropTypes.object,
    broadcasterPromo: PropTypes.object,
    scrollToRound: PropTypes.number,
    setScrollToRound: PropTypes.func
};
